import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Loader2, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Logo Component
const Logo = ({ className = "h-24" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Password Validation - stesse regole della registrazione
const validatePassword = (password) => {
  const errors = [];
  
  if (!password) {
    return { isValid: false, errors: ['Inserisci una password'] };
  }
  
  if (password.length < 12) {
    errors.push('Almeno 12 caratteri');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Almeno una lettera maiuscola (A-Z)');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Almeno una lettera minuscola (a-z)');
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Almeno un numero (0-9)');
  }
  
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) {
    errors.push('Almeno un carattere speciale (es. !, @, #, $, %)');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // 'checking' | 'valid' | 'invalid' - stato del link di recupero
  const [linkState, setLinkState] = useState('checking');
  const [linkError, setLinkError] = useState('');

  // Verifica che il link di recupero abbia aperto una sessione valida.
  // detectSessionInUrl consuma il token in modo asincrono, quindi aspettiamo
  // l'evento di auth prima di dichiarare il link non valido.
  useEffect(() => {
    let cancelled = false;

    const markValid = () => {
      if (!cancelled) setLinkState('valid');
    };

    const run = async () => {
      // Caso 1: Supabase segnala un errore nel frammento dell'URL (link scaduto o già usato)
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const hashError = hashParams.get('error_description') || hashParams.get('error');
      if (hashError) {
        if (!cancelled) {
          setLinkError(decodeURIComponent(hashError.replace(/\+/g, ' ')));
          setLinkState('invalid');
        }
        return;
      }

      // Caso 2: template con token_hash nella query string
      const queryParams = new URLSearchParams(window.location.search);
      const tokenHash = queryParams.get('token_hash');
      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
        if (cancelled) return;
        if (error) {
          setLinkError('Il link di recupero non è più valido.');
          setLinkState('invalid');
        } else {
          markValid();
        }
        return;
      }

      // Caso 3: sessione già aperta dal link
      const { data } = await supabase.auth.getSession();
      if (!cancelled && data?.session) markValid();
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        markValid();
      }
    });

    run();

    // Ultima verifica: se dopo l'attesa non c'è sessione, il link non è valido
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setLinkState((prev) => (prev === 'valid' ? prev : (data?.session ? 'valid' : 'invalid')));
    }, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const check = validatePassword(password);
    if (!check.isValid) {
      toast.error(check.errors[0]);
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Le due password non coincidono');
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      setSuccess(true);
      toast.success('Password aggiornata');
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (error) {
      console.error('Password update error:', error);
      const isSamePassword = (error?.message || '').toLowerCase().includes('different from the old password');
      toast.error(
        isSamePassword
          ? 'La nuova password deve essere diversa dalla precedente'
          : 'Non è stato possibile aggiornare la password. Richiedi un nuovo link.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Verifica del link in corso
  if (linkState === 'checking') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#4A7C59] mx-auto mb-4" />
          <p className="text-[#64748B]">Verifica del link in corso...</p>
        </div>
      </div>
    );
  }

  // Link scaduto o non valido
  if (linkState === 'invalid') {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
        <header className="p-4 sm:p-6">
          <button 
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#4A7C59] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Torna al login</span>
          </button>
        </header>
        
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <Logo className="h-28 mx-auto mb-4" />
            </div>
            
            <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#FEF3C7] flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-8 h-8 text-[#D97706]" />
              </div>
              <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
                Link non più valido
              </h1>
              <p className="text-[#64748B] mb-2">
                Il link per reimpostare la password è scaduto oppure è già stato utilizzato.
              </p>
              <p className="text-[#64748B] mb-8">
                Richiedine uno nuovo dalla pagina di accesso.
              </p>
              {linkError && (
                <p className="text-xs text-[#9CA3AF] mb-6">{linkError}</p>
              )}
              
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
              >
                Richiedi un nuovo link
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Password aggiornata
  if (success) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo className="h-28 mx-auto mb-4" />
          </div>
          
          <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#DCFCE7] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#4A7C59]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2C3E50] mb-3">
              Password aggiornata
            </h1>
            <p className="text-[#64748B] mb-8">
              Da ora puoi accedere con la tua nuova password.
            </p>
            
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
            >
              Vai alla Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Form nuova password
  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <header className="p-4 sm:p-6">
        <button 
          onClick={() => navigate('/login')}
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#4A7C59] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Torna al login</span>
        </button>
      </header>
      
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Logo className="h-28 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#2C3E50]">
              Nuova Password
            </h1>
            <p className="text-[#64748B] mt-2">
              Scegli una nuova password per il tuo account
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Nuova password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-14 pl-12 pr-12 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4A7C59] focus:outline-none transition-colors"
                    placeholder="Minimo 12 caratteri"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#64748B]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-xs text-[#9CA3AF] mt-2 space-y-0.5">
                  <p>- Almeno 12 caratteri</p>
                  <p>- Almeno una lettera maiuscola e una minuscola</p>
                  <p>- Almeno un numero e un carattere speciale</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#2C3E50] mb-2">
                  Conferma password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full h-14 pl-12 pr-4 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4A7C59] focus:outline-none transition-colors"
                    placeholder="Ripeti la password"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Salvataggio...
                  </>
                ) : (
                  'Salva Nuova Password'
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
